import { useMemo, type Context, type ReactNode } from 'react';
import type { AuthContextValue, EntraProviderConfig, SignInInput } from './types';

interface Props {
  config: EntraProviderConfig;
  context: Context<AuthContextValue | null>;
  children: ReactNode;
}

// Dormant. Kept so the provider abstraction in @deltasuite/auth survives the
// switch to Supabase Auth. When IT unblocks the Entra security review, plug
// MSAL (@azure/msal-browser + @azure/msal-react) into this provider's body
// and flip VITE_AUTH_PROVIDER to 'entra' — apps won't change.
//
// While dormant the provider stays in 'signed_out' and refuses sign-in. That
// way an accidental config flip surfaces a hard error instead of silently
// landing users on a broken flow.
export function EntraProvider({ config, context: AuthContext, children }: Props) {
  void config; // tenantId / clientId / redirectUri kept for the future MSAL wiring
  const value = useMemo<AuthContextValue>(
    () => ({
      state: { status: 'signed_out' },
      signIn: async (_input: SignInInput) => {
        throw new Error(
          'EntraProvider is dormant in v1. Set VITE_AUTH_PROVIDER=supabase or unblock the Entra IT review.',
        );
      },
      signOut: async () => {
        // No-op while dormant.
      },
      getAccessToken: async () => null,
      realtimeClient: null,
    }),
    [],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
